import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Checkbox, FormControlLabel, IconButton, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Field } from '../../UI';
import { LinkAdicionar } from '../abas/BlocosCadastrais';
import * as C from '../../../theme/colors.constants';

/**
 * E-mails da aba Devedor (AW2-1134 Etapa 5) — porta de `SimpleEmails.jsx`
 * real, componente novo (sem equivalente mockado no protótipo até agora).
 * Mesmo padrão estrutural de `EnderecoDevedor.jsx`/`TelefoneDevedor.jsx`,
 * mas mais simples — só campo E-mail + "Principal".
 *
 * Ícone de lixeira aparece em TODOS os itens, inclusive o índice 0 — mesmo
 * comportamento de Telefones (Etapa 4), confirmado por leitura direta:
 * `EmailDevedor.jsx` real não passa `showTrashAtFirstItem` para
 * `SimpleEmails.jsx` (default `false`, que faz `showTrashAtFirstItemHandler`
 * retornar sempre `true`). Diferente de Endereços (Etapa 3), que sempre
 * esconde a lixeira do índice 0 — não assumir paridade entre componentes
 * "irmãos" de lista sem checar o wrapper real de cada um (lição já
 * registrada em `src/prototipo/CLAUDE.md`).
 *
 * Sem validação de formato de e-mail ao vivo — `SimpleEmail.jsx` real
 * também não valida no client (só exibe `model.email.error`, populado por
 * erro 400 do backend pós-submit); consistente com a decisão já tomada de
 * deixar essa validação para a Etapa 9 (submit real).
 */
export default function EmailDevedor({
  emails, podeModificarFase, isEditMode, onChangeEmail, onAdicionarEmail, onRemoverEmail, onMarcarPrincipal,
}) {
  const desabilitado = !podeModificarFase || !isEditMode;

  return (
    <>
      <LinkAdicionar onClick={onAdicionarEmail} disabled={desabilitado}>
        Adicionar E-mail +
      </LinkAdicionar>

      {emails.map((email, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.5fr) minmax(0, 1fr)' },
            gap: 'clamp(10px, 0.8vw, 22px)',
            alignItems: 'end',
            mb: 1.5,
          }}
        >
          <Field
            label="E-mail:"
            name="email"
            value={email.email}
            onChange={e => onChangeEmail(index, 'email', e.target.value)}
            disabled={desabilitado}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={Boolean(email.principal)}
                  onChange={() => onMarcarPrincipal(index)}
                  disabled={desabilitado}
                />
              )}
              label={<Typography variant="body2">Principal</Typography>}
              sx={{ ml: 0 }}
            />
            <Tooltip title="Excluir E-mail">
              <span>
                <IconButton
                  size="small"
                  aria-label="Excluir E-mail"
                  onClick={() => onRemoverEmail(index)}
                  disabled={desabilitado}
                  sx={{ color: C.navy }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </>
  );
}

EmailDevedor.propTypes = {
  emails: PropTypes.arrayOf(PropTypes.shape({
    idDevedorEmail: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    email: PropTypes.string,
    principal: PropTypes.bool,
  })),
  podeModificarFase: PropTypes.bool,
  isEditMode: PropTypes.bool,
  onChangeEmail: PropTypes.func,
  onAdicionarEmail: PropTypes.func,
  onRemoverEmail: PropTypes.func,
  onMarcarPrincipal: PropTypes.func,
};

EmailDevedor.defaultProps = {
  emails: [],
  podeModificarFase: false,
  isEditMode: false,
  onChangeEmail: () => null,
  onAdicionarEmail: () => null,
  onRemoverEmail: () => null,
  onMarcarPrincipal: () => null,
};
